// XP + Leaderboard helpers
import { supabase } from "@/integrations/supabase/client";

export type XpSummary = {
  totalXp: number;
  currentStreak: number;
  longestStreak: number;
  lastDate: string | null;
};

export async function fetchMyXp(userId: string): Promise<XpSummary> {
  const { data } = await supabase
    .from("user_xp")
    .select("total_xp, current_streak, longest_streak, last_completed_date")
    .eq("user_id", userId)
    .maybeSingle();

  return {
    totalXp: data?.total_xp ?? 0,
    currentStreak: data?.current_streak ?? 0,
    longestStreak: data?.longest_streak ?? 0,
    lastDate: data?.last_completed_date ?? null,
  };
}

// Duolingo-style levels for WCC360
export type Level = {
  name: string;
  min: number;
  emoji: string;
  blurb: string;
};

export const LEVELS: Level[] = [
  { name: "Seed", min: 0, emoji: "🌱", blurb: "Just getting rooted." },
  { name: "Explorer", min: 100, emoji: "🧭", blurb: "Finding your direction." },
  { name: "Creator", min: 300, emoji: "🎨", blurb: "Making things happen." },
  { name: "Navigator", min: 700, emoji: "🗺️", blurb: "Charting real pathways." },
  { name: "Visionary", min: 1500, emoji: "🔮", blurb: "Seeing what's next." },
  { name: "Purpose Champion", min: 3000, emoji: "🏆", blurb: "Living your why." },
  { name: "Future Builder", min: 6000, emoji: "🚀", blurb: "Shaping the future of work." },
];

export function levelFor(xp: number): { current: Level; next: Level | null; progressPct: number } {
  let current = LEVELS[0];
  let next: Level | null = LEVELS[1] ?? null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (xp >= LEVELS[i].min) {
      current = LEVELS[i];
      next = LEVELS[i + 1] ?? null;
    }
  }
  const span = next ? next.min - current.min : 1;
  const into = Math.max(0, xp - current.min);
  const progressPct = next ? Math.min(100, Math.round((into / span) * 100)) : 100;
  return { current, next, progressPct };
}

export type LeaderboardRow = {
  rank: number;
  user_id: string;
  display_name: string;
  handle: string | null;
  total_xp?: number;
  current_streak?: number;
  converted_count?: number;
};

export async function fetchXpLeaderboard(limit = 25): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase.rpc("get_xp_leaderboard", { _limit: limit });
  if (error) return [];
  return (data ?? []).map((r: any) => ({
    rank: Number(r.rank),
    user_id: r.user_id,
    display_name: r.display_name,
    handle: r.handle,
    total_xp: r.total_xp,
    current_streak: r.current_streak,
  }));
}

export async function fetchReferralLeaderboard(limit = 25): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase.rpc("get_referral_leaderboard", { _limit: limit });
  if (error) return [];
  return (data ?? []).map((r: any) => ({
    rank: Number(r.rank),
    user_id: r.user_id,
    display_name: r.display_name,
    handle: r.handle,
    converted_count: Number(r.converted_count),
  }));
}
